import { useEffect, useState } from "react";
import { TopBarLayout } from "@timeedit/ui-components/components/Topbar/TopbarLayout";
import { TopBarContent } from "@timeedit/ui-components/components/Topbar/TopbarContent";
import { EAppId } from "@timeedit/types/lib/enums/appIds.enum";
import { EAppEnv } from "@timeedit/types/lib/enums/appEnv.enum";
import ViewMenu from "./ViewMenu";
import DraftList from "./DraftList";
import HelpMenu from "./HelpMenu";
import { View } from "../../models/View";
import { ITEToken, TOrganizationPublic } from "@timeedit/types";
import { User } from "../../models/User";
import Language from "../../lib/Language";
import { ConfigProvider, MenuProps } from "@timeedit/ui-components/antd";
import { ItemType } from "@timeedit/ui-components/antd/menu/interface";
import {
    CalendarOutlined,
    ClockCircleOutlined,
    OpenInNewTab,
    TranslationOutlined,
} from "@timeedit/ui-components/components/Icon/Icons/index";
import TopbarPinboardButton from "./TopbarPinboardButton";
import TopbarSettingsWindow from "./TopbarSettingsWindow";
import Tooltip from "@timeedit/ui-components/components/Tooltip/Tooltip";
import { useAppContext } from "../../contexts/AppContext";
import API from "../../lib/TimeEditAPI";
import { TComponent } from "../../types/utils";
import Icon from "@timeedit/ui-components/components/Icon/Icon";
import TooltippedTopbarButton from "./TooltippedTopbarButton";
import { TOOLTIP_DELAY, TOPBAR_BUTTON_ICON_HEIGHT } from "../../lib/UIConstants";
import Logotype from "@timeedit/ui-components/components/Logotype/Logotype";
import { TReservationLayer } from "../../types/models/TReservationLayer";

type TTopBarProps = {
    appEnv: EAppEnv;
    organization: TOrganizationPublic;
    user: ITEToken;
    coreUser?: User | null | undefined;
    createIssue: () => void;
    toggleShortcutOverlay: () => void;
    toggleAboutDialog: (supportInfoLines: string[], showSupportText: boolean) => void;
    handleViewChange: (id: number) => void;
    view: View;
    layers: TReservationLayer[];
    activeLayer: number;
    setActiveLayer: (layerId: number) => void;
    onLayerCreated: (layerId: number, move: boolean) => void;
    onRemoveLayer: () => void;
    reloadLayers: (callback?: () => void) => void;
    useTEUIStep2: boolean;
    onLogout: () => void;
    onAdminClicked: MenuProps["onClick"];
    onResetHelpClicked: MenuProps["onClick"];
};

const getUserLanguageLabel = (
    identifier: string | undefined,
    languages: { id: string; label: string }[]
): string => {
    if (!identifier) {
        return Language.get("cal_res_side_user_lang_system");
    }
    return (
        languages.find((lang) => lang.id === identifier)?.label ||
        Language.get("cal_res_side_user_lang_system")
    );
};
const getDateFormatLabel = (
    identifier: string | undefined,
    languages: { id: string; label: string }[]
): string => {
    if (!identifier) {
        return Language.get("cal_res_side_user_lang_system");
    }
    return (
        languages.find((lang) => lang.id === identifier)?.label ||
        Language.get("cal_res_side_user_lang_system")
    );
};

const CoreTopBar: TComponent<TTopBarProps> = (props) => {
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [languages, setLanguages] = useState<{ id: string; label: string }[]>([]);
    const [dateFormats, setDateFormats] = useState<{ id: string; label: string }[]>([]);
    const [timezones, setTimezones] = useState<
        { shortName: string; name: string; offset: string }[]
    >([]);

    const appContext = useAppContext();
    const { user } = appContext;

    useEffect(() => {
        API.getTimezones((tz) => {
            setTimezones(tz);
        });
        API.getLanguages((result) => {
            result.unshift({ id: "", label: Language.get("cal_res_side_user_lang_system") });
            setLanguages(result);
        }, true);
        API.getLanguages((result) => {
            result.unshift({ id: "", label: Language.get("cal_res_side_user_lang_system") });
            setDateFormats(result);
        }, false);
    }, [user?.language]);

    const saveUser = (newProps: Record<string, string>) => {
        const newUser = user?.immutableSet(newProps);
        newUser?.save((success) => {
            if (!success) {
                return;
            }
            document.location.reload();
        });
    };

    const changeLanguage = (newLanguage: string) => {
        saveUser({ language: newLanguage });
    };

    const changeDateFormat = (newDateFormat: string) => {
        saveUser({ dateFormat: newDateFormat });
    };

    const changeTimezone = (newTimezone: string) => {
        saveUser({ timezone: newTimezone });
    };

    const timezoneOptions = timezones.map((zone) => ({
        value: zone.shortName,
        label: zone.name !== zone.shortName ? `${zone.shortName} ${zone.name}` : zone.shortName,
    }));

    let userMenuItems: ItemType[] = props.useTEUIStep2
        ? [
              {
                  key: "language",
                  label: `${Language.get("cal_res_side_user_language")}: ${getUserLanguageLabel(
                      user?.language,
                      languages
                  )}`,
                  icon: <TranslationOutlined aria-hidden={true} />,
                  children: languages.map((language) => {
                      return {
                          key: language.id,
                          label: language.label,
                          onClick: () => {
                              changeLanguage(language.id);
                          },
                      };
                  }),
              },
              {
                  key: "timezone",
                  // The timezone in use, which is not the one chosen if the server lacks it.
                  label: `${Language.get("cal_res_side_user_time_zone")}: ${
                      user?.actualTimezone?.shortName ?? user?.timezone
                  }`,
                  icon: <ClockCircleOutlined aria-hidden={true} />,
                  children: timezoneOptions.map((tz) => {
                      return {
                          key: tz.value,
                          label: tz.label,
                          onClick: () => {
                              changeTimezone(tz.value);
                          },
                      };
                  }),
              },
              {
                  label: `${Language.get("cal_res_side_user_date_format")}: ${getDateFormatLabel(
                      user?.dateFormat,
                      dateFormats
                  )}`,
                  key: "dateformat",
                  icon: <CalendarOutlined aria-hidden={true} />,
                  children: dateFormats.map((format) => {
                      return {
                          key: format.id,
                          label: format.label,
                          onClick: () => {
                              changeDateFormat(format.id);
                          },
                      };
                  }),
              },
          ]
        : [];

    if (props.coreUser?.isAdmin) {
        const adminMenuItems: ItemType[] = [
            {
                key: "admin",
                label: Language.get("admin_main_menu_title"),
                itemIcon: <OpenInNewTab aria-hidden={true} />,
                onClick: props.onAdminClicked,
            },
        ];
        userMenuItems = [...userMenuItems, ...adminMenuItems];
    }

    const topBarElements = {
        left: (
            <>
                <TopBarContent.ProductSelector
                    appEnv={props.appEnv}
                    currentProduct={EAppId.TE_CORE}
                    organization={props.organization}
                    user={props.user}
                />
                <TopBarContent.OrganizationName organization={props.organization} />
                <TopBarContent.EnvTag appEnv={props.appEnv} />
            </>
        ),
        center: <ViewMenu view={props.view} changeView={props.handleViewChange} />,
        right: (
            <>
                {props.useTEUIStep2 && <TopbarPinboardButton />}
                {props.useTEUIStep2 && (
                    <DraftList
                        appearance="button"
                        layers={props.layers}
                        activeLayer={props.activeLayer}
                        setActiveLayer={props.setActiveLayer}
                        onLayerCreated={props.onLayerCreated}
                        onRemoveLayer={props.onRemoveLayer}
                        reloadLayers={props.reloadLayers}
                    />
                )}
                {props.useTEUIStep2 && (
                    <TooltippedTopbarButton
                        tooltip={Language.get("nc_settings")}
                        icon={
                            <Icon
                                id="SettingOutlined"
                                alt={Language.get("nc_settings")}
                                size={TOPBAR_BUTTON_ICON_HEIGHT}
                            />
                        }
                        onClick={() => setSettingsOpen(!settingsOpen)}
                    />
                )}
                {props.useTEUIStep2 && (
                    <TopbarSettingsWindow
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                    />
                )}
                <Tooltip mouseEnterDelay={TOOLTIP_DELAY} title={Language.get("login_user")}>
                    <>
                        <TopBarContent.UserDropdown
                            menu={{ items: userMenuItems }}
                            onLogout={props.onLogout}
                            user={props.user as any}
                        />
                    </>
                </Tooltip>
                <HelpMenu
                    onHelpCenterClicked={props.createIssue}
                    onShortcutsClicked={props.toggleShortcutOverlay}
                    onAboutClicked={props.toggleAboutDialog}
                    onResetHelpClicked={props.onResetHelpClicked}
                />
                <div className="tw:flex tw:items-center tw:px-2">
                    <Logotype alt="TimeEdit" id="TE" size={24} />
                </div>
            </>
        ),
    };

    return (
        <ConfigProvider
            theme={{
                components: {
                    Menu: {
                        itemHeight: 30,
                    },
                },
            }}
        >
            <TopBarLayout currentProduct={EAppId.TE_CORE} slots={topBarElements} />
        </ConfigProvider>
    );
};

export default CoreTopBar;
